import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicNavHeader from '../components/PublicNavHeader';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

import {
  ArrowRight,
  CheckCircle,
  ChartLine,
  ClipboardText,
  GearSix,
  Lightning,
  Lock,
  Target,
  UsersThree
} from '@phosphor-icons/react';

const CommandPage = () => {
  const { authStatus, user } = useAuth();
  const isAuthenticated = authStatus === 'authenticated' && user;

  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  return (
    <>
      <Helmet>
        <title>Command - Talk to Your Business | Man & Machine</title>
        <meta name="description" content="Create workers, run tasks, and automate work using simple text or voice commands. Just describe what you want and Command builds and runs it instantly." />
        <meta name="keywords" content="AI command interface, voice AI, text commands, workflow automation, business automation, AI workers" />
        <link rel="canonical" href="https://manandmachine.ai/command" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://manandmachine.ai/command" />
        <meta property="og:title" content="Command - Talk to Your Business | Man & Machine" />
        <meta property="og:description" content="Create workers, run tasks, and automate work using simple text or voice commands." />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Command - Talk to Your Business | Man & Machine" />
        <meta name="twitter:description" content="Create workers, run tasks, and automate work using simple text or voice commands." />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Man & Machine Command",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "Create workers, run tasks, and automate work using simple text or voice commands.",
            "publisher": {
              "@type": "Organization",
              "name": "Man & Machine",
              "url": "https://manandmachine.ai"
            }
          })}
        </script>
      </Helmet>

      <PublicNavHeader />

      <div className="min-h-screen bg-[#161B26]">

        {/* ═══ HERO ═══ */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,229,192,0.06)] via-transparent to-[rgba(0,229,192,0.03)]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] sm:w-[800px] h-[300px] sm:h-[400px] bg-[rgba(0,229,192,0.08)] blur-[120px] rounded-full" />

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.2)] mb-6">
                <Lightning size={14} className="text-[#00E5C0]" />
                <span className="text-xs text-[#A0AEC0]">Command Interface</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                Talk to Your Business
              </h1>

              <p className="text-base sm:text-lg text-[#A0AEC0] mb-3 px-2 max-w-2xl mx-auto leading-relaxed">
                Create workers, run tasks, and automate work using simple text or voice commands.
              </p>

              <p className="text-sm text-[#7D8896] mb-8 px-2">
                Just describe what you want - Command builds and runs it instantly.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8">
                <Link
                  to={isAuthenticated ? "/dashboard" : "/register"}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold text-lg rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)]"
                  data-testid="command-hero-cta"
                >
                  Try Command
                  <ArrowRight size={18} />
                </Link>
                <button
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white font-semibold text-lg rounded-xl hover:bg-[rgba(255,255,255,0.08)] transition-all"
                  data-testid="command-learn-more"
                >
                  See How It Works
                </button>
              </div>

              <p className="text-sm text-[#A0AEC0] italic">
                If you can describe it, Command can build it.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ═══ WHAT YOU CAN DO WITH COMMAND ═══ */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-2xl sm:text-3xl font-bold text-white mb-8 sm:mb-10 text-center"
          >
            What You Can Do With <span className="text-[#00E5C0]">Command</span>
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                icon: Lightning,
                title: 'Create Workers Instantly',
                desc: 'Describe what you want and Command builds the worker for you',
                color: '#00E5C0'
              },
              {
                icon: GearSix,
                title: 'Run Work Automatically',
                desc: 'Trigger tasks, workflows, and actions in seconds',
                color: '#EC4899'
              },
              {
                icon: ChartLine,
                title: 'Ask Your Business Questions',
                desc: 'Get answers from your data, systems, and activity',
                color: '#F59E0B'
              },
              {
                icon: ClipboardText,
                title: 'Control Everything in One Place',
                desc: 'No dashboards or menus - just tell it what to do',
                color: '#8B5CF6'
              }
            ].map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: i * 0.1 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.25)] transition-colors"
                data-testid={`command-card-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${card.color}15` }}
                >
                  <card.icon size={24} style={{ color: card.color }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-[#A0AEC0] text-sm">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ═══ JUST TELL IT WHAT YOU WANT ═══ */}
        <section className="py-16 sm:py-24 bg-[rgba(255,255,255,0.02)]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="text-center mb-10"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Just Tell It <span className="text-[#00E5C0]">What You Want</span>
              </h2>
            </motion.div>

            <div className="space-y-3">
              {[
                'Create a worker that responds to new leads instantly',
                'Follow up all unpaid invoices from last week',
                'Book this customer in for tomorrow morning',
                'Show me all jobs scheduled today'
              ].map((example, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: i * 0.08 }}
                  className="flex items-center gap-3 p-4 glass rounded-xl border border-[rgba(255,255,255,0.05)]"
                  data-testid={`example-${i}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-[rgba(0,229,192,0.1)] flex items-center justify-center flex-shrink-0">
                    <Lightning size={16} className="text-[#00E5C0]" />
                  </div>
                  <span className="text-[#E2E8F0] text-sm italic">"{example}"</span>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.3 }}
              className="text-center text-sm text-[#7D8896] mt-8"
            >
              No setup. No menus. Just tell Command what to do.
            </motion.p>
          </div>
        </section>

        {/* ═══ FROM IDEA TO WORKER IN SECONDS ═══ */}
        <section id="how-it-works" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="text-2xl sm:text-3xl font-bold text-white mb-10 sm:mb-14 text-center"
            >
              From Idea to Worker in <span className="text-[#00E5C0]">Seconds</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { num: '1', title: 'Describe what you need', color: '#00E5C0' },
                { num: '2', title: 'Command builds the worker', color: '#EC4899' },
                { num: '3', title: 'Review and activate', color: '#F59E0B' }
              ].map((step, i) => (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: i * 0.12 }}
                  className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] text-center"
                  data-testid={`creation-step-${step.num}`}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${step.color}15` }}
                  >
                    <span className="font-bold text-lg" style={{ color: step.color }}>{step.num}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.3 }}
              className="text-center text-[#A0AEC0] text-sm mt-8"
            >
              No forms. No configuration. Just describe and go.
            </motion.p>
          </div>
        </section>

        {/* ═══ BUILT-IN CONTROL AND SAFETY ═══ */}
        <section className="py-16 sm:py-24 bg-[rgba(255,255,255,0.02)]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="text-center mb-10"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Built-In <span className="text-[#00E5C0]">Control and Safety</span>
              </h2>
              <p className="text-[#A0AEC0]">
                You stay in control of every action your workers take
              </p>
            </motion.div>

            <div className="space-y-3">
              {[
                'Actions require confirmation when needed',
                'Clear preview before anything runs',
                'Full activity tracking and logs',
                'Role-based permissions',
                'Ability to approve, cancel, or edit actions'
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: i * 0.08 }}
                  className="flex items-center gap-3 p-4 glass rounded-xl border border-[rgba(255,255,255,0.05)]"
                >
                  <CheckCircle size={18} className="text-[#00E5C0] flex-shrink-0" />
                  <span className="text-[#E2E8F0] text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ YOU'RE ALWAYS IN CONTROL ═══ */}
        <section className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="text-2xl sm:text-3xl font-bold text-white mb-10 text-center"
            >
              You're Always in <span className="text-[#00E5C0]">Control</span>
            </motion.h2>

            <div className="glass rounded-2xl p-7 border border-[rgba(0,229,192,0.15)]">
              <div className="space-y-4">
                {[
                  'Command never acts without your approval for sensitive actions',
                  'You see exactly what will happen before anything runs',
                  'All actions are tracked and visible',
                  'Nothing runs in the background without you knowing'
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Lock size={16} className="text-[#00E5C0] flex-shrink-0 mt-0.5" />
                    <span className="text-[#E2E8F0] text-sm">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══ FINAL CTA ═══ */}
        <section className="py-16 sm:py-24 bg-[rgba(255,255,255,0.02)]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass rounded-2xl p-8 sm:p-10 text-center border border-[rgba(0,229,192,0.15)]"
              data-testid="command-final-cta"
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                Start Using <span className="text-[#00E5C0]">Command</span>
              </h2>
              <p className="text-[#A0AEC0] mb-8 max-w-xl mx-auto">
                Create and run workers using simple commands - no setup required.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Link
                  to={isAuthenticated ? "/dashboard" : "/register"}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold text-lg rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)]"
                  data-testid="cta-try-command"
                >
                  Try Command
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to={isAuthenticated ? "/dashboard" : "/login"}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white font-semibold text-lg rounded-xl hover:bg-[rgba(255,255,255,0.08)] transition-all"
                  data-testid="cta-dashboard"
                >
                  Go to Dashboard
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

      </div>

      <Footer />
    </>
  );
};

export default CommandPage;
